import { useEffect, useState } from "react";
import CustomButton from "../CustomButton";
import ModalLayout from "../layouts/ModalLayout";
import QueryApis from "../../api/QueryApis";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { fetchQueries } from "../../features/query/QuerySlice";
import ConfirmationModal from "./ConfirmationModal";
import CustomInput from "../CustomInput";
import { toast } from "sonner";

interface Props {
  onClose?: () => void;
  onSave?: (selectedStatus: string) => void;
}

export default function QueriesStatus({ onClose, onSave }: Props) {
  const [statusList, setStatusList] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [visitDate, setVisitDate] = useState<string>("");

  const showVisitDate =
    selected === "VISIT_SCHEDULED" || selected === "VISIT_RESCHEDULED";

  const location = useLocation();
  const { queryId } = location.state as { queryId: number };
  const dispatch = useDispatch<AppDispatch>();
  const [pagination] = useSelector((state: RootState) => [
    state.query.pagination,
  ]);

  useEffect(() => {
    const loadStatus = async () => {
      const result = await QueryApis.queriesStatus();
      if (result?.status === 1 && Array.isArray(result.data)) {
        setStatusList(result.data);
      }
      setLoading(false);
    };

    loadStatus();
  }, []);

  const handleSaveStatus = async () => {
    const result = await QueryApis.moveQueryToStatus(
      queryId,
      selected,
      selected === "VISIT_SCHEDULED" || selected === "VISIT_RESCHEDULED"
        ? visitDate
        : undefined
    );

    if (result?.status === 1) {
      onSave?.(selected);
      onClose?.();
      dispatch(
        fetchQueries({
          page: pagination?.number || 0,
          size: pagination?.size || 10,
        })
      );
    }
  };

  const handleSaveClick = () => {
    if (showVisitDate && !visitDate) {
      toast.error("Please select visit date");
      return;
    }

    if (selected === "ADMISSION_OFFERED") {
      setShowConfirm(true);
    } else {
      handleSaveStatus();
    }
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    handleSaveStatus();
  };

  return (
    <>
      <ModalLayout onClose={onClose} width="max-w-md">
        <div className="flex flex-col gap-6">
          <h4 className="text-xl font-semibold text-neutral-400">
            Queries Status
          </h4>

          <div className="flex flex-col gap-3 max-h-72 overflow-y-auto custom-scrollbar">
            {statusList.map((status) => (
              <label
                key={status}
                className="flex items-center gap-3 cursor-pointer"
              >
                <input
                  type="radio"
                  name="status"
                  value={status}
                  checked={selected === status}
                  onChange={() => setSelected(status)}
                  className="w-5 h-5 cursor-pointer text-primary-500"
                />
                <span className="text-neutral-400">{status}</span>
              </label>
            ))}
          </div>

          {showVisitDate && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold text-neutral-400">
                Visit Date
              </label>
              <CustomInput
                name="visitDate"
                type="date"
                value={visitDate}
                onChange={(e) => setVisitDate(e.target.value)}
              />
            </div>
          )}

          <div className="flex justify-end items-end gap-4 pt-3">
            <CustomButton
              text="Close"
              color="bg-neutral-200"
              onClick={onClose}
            />
            <CustomButton
              text="Save"
              color="bg-success-600"
              disabled={!selected}
              onClick={handleSaveClick}
            />
          </div>
        </div>
      </ModalLayout>

      {showConfirm && (
        <ConfirmationModal
          title="Status Change"
          message="Are you sure you want to mark this query as ADMISSION OFFERED?"
          confirmText="Yes"
          cancelText="No"
          confirmColor="bg-success-600"
          onClose={() => setShowConfirm(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
